// Compact keynote-style top navigation. Replaces the permanent SidePanel +
// TopChapterBar. Chapter jumps, Contents drawer, Share, Present toggle,
// theme, sign-out, and a lightweight "Watch demos" overlay.
import { useEffect, useState } from "react";
import { createPortal } from "react-dom";
import {
  Share2,
  Maximize2,
  Minimize2,
  LogOut,
  Sun,
  Moon,
  Menu,
  X,
  ArrowUpRight,
} from "lucide-react";
import { chapters } from "../data/slides";
import { useTheme } from "../context/ThemeContext";
import { useAirline } from "../context/AirlineContext";
import Logo from "../shared/Logo";

const AEROPLAY_ICON =
  "https://customer-assets.emergentagent.com/job_24108f3e-b858-4890-993f-f1e1a60b89e6/artifacts/nmitj80k_Aeroplay-03-small.png";
const AEROPLAY_WORDMARK_DARK =
  "https://customer-assets.emergentagent.com/job_24108f3e-b858-4890-993f-f1e1a60b89e6/artifacts/h1sxdkcz_Aeroplay%20logo%202026_White.png";
const AEROPLAY_WORDMARK_LIGHT =
  "https://customer-assets.emergentagent.com/job_24108f3e-b858-4890-993f-f1e1a60b89e6/artifacts/oot4cqoo_Aeroplay%20logo%202026_Black.png";
const NUCLEUS_BRIDGE = "https://customer-assets-lxgj4vgw.emergentagent.net/job_24108f3e-b858-4890-993f-f1e1a60b89e6/artifacts/0zo9n6c8_NucluesOS_Brand-11.webp";
const NUCLEUS_LENS = "https://customer-assets-lxgj4vgw.emergentagent.net/job_24108f3e-b858-4890-993f-f1e1a60b89e6/artifacts/5sciu7sm_NucluesOS_Brand-12.webp";

const PitchNav = ({ currentSlide, setCurrentSlide, slideList }) => {
  const { isDark, toggleTheme } = useTheme();
  const { airline } = useAirline();
  const isPrivate = airline && airline.id !== "default";

  const [contentsOpen, setContentsOpen] = useState(false);
  const [demosOpen, setDemosOpen] = useState(false);
  const [isFullscreen, setIsFullscreen] = useState(false);
  const [shareToast, setShareToast] = useState("");

  useEffect(() => {
    const onChange = () => setIsFullscreen(!!document.fullscreenElement);
    document.addEventListener("fullscreenchange", onChange);
    return () => document.removeEventListener("fullscreenchange", onChange);
  }, []);

  useEffect(() => {
    const onKey = (e) => {
      if (e.key === "Escape") {
        setContentsOpen(false);
        setDemosOpen(false);
      }
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, []);

  const activeChapter = slideList[currentSlide]?.chapter;
  const compactNav = (airline.chapters || []).length > 7;
  const chapterPad = compactNav ? "px-2 text-[12.5px]" : "px-3 text-[13px]";

  // Which chapters actually exist in this pitch (deduped, ordered)
  const visibleChapters = (() => {
    const seen = new Set();
    return (airline.chapters || chapters).filter((c) => {
      if (seen.has(c.id)) return false;
      const idx = slideList.findIndex((s) => s.chapter === c.id);
      if (idx === -1) return false;
      seen.add(c.id);
      return true;
    });
  })();

  const jumpChapter = (chapterId) => {
    const idx = slideList.findIndex((s) => s.chapter === chapterId);
    if (idx !== -1) setCurrentSlide(idx);
  };

  const togglePresent = async () => {
    try {
      if (!document.fullscreenElement) await document.documentElement.requestFullscreen();
      else await document.exitFullscreen();
    } catch {}
  };

  const handleShare = async () => {
    const isOnPitchRoute = /^\/pitch(\/.*)?$/.test(window.location.pathname);
    const url = isOnPitchRoute
      ? `${window.location.origin}${window.location.pathname}`
      : "https://aeroplay.app/";
    const text = isOnPitchRoute
      ? "Your Aeroplay presentation is available here. Please use the password provided separately."
      : "Discover Aeroplay's approach to airline entertainment.";
    try {
      if (navigator.share) return await navigator.share({ title: "Aeroplay", text, url });
      await navigator.clipboard.writeText(url);
      setShareToast("Link copied");
      setTimeout(() => setShareToast(""), 1800);
    } catch {}
  };

  const handleLogout = async () => {
    if (!isPrivate || !airline.code) return;
    try {
      const API = process.env.REACT_APP_BACKEND_URL;
      await fetch(`${API}/api/pitch/${airline.code}/logout`, {
        method: "POST",
        credentials: "include",
      });
    } catch {}
    window.location.href = "/";
  };

  const barBg = isDark
    ? "bg-[#0b0b0d]/85 border-b border-white/[0.08]"
    : "bg-[#fbfbf9]/85 border-b border-black/[0.06]";

  return (
    <>
      <header
        data-testid="pitch-nav"
        className={`fixed top-0 inset-x-0 z-40 ${barBg}`}
        style={{ backdropFilter: "saturate(180%) blur(20px)", WebkitBackdropFilter: "saturate(180%) blur(20px)" }}
      >
        <div className="mx-auto max-w-[1320px] px-6 lg:px-10 h-14 flex items-center gap-6">
          {/* Logo */}
          <button
            onClick={() => setCurrentSlide(0)}
            data-testid="nav-logo"
            className="flex items-center flex-shrink-0 mr-4"
          >
            <img
              src={isDark ? AEROPLAY_WORDMARK_DARK : AEROPLAY_WORDMARK_LIGHT}
              alt="Aeroplay"
              className="h-11 w-auto object-contain"
            />
          </button>

          {/* Airline (private) — subtle chip */}
          {isPrivate && (
            <div
              data-testid="airline-chip"
              className={`hidden lg:flex items-center gap-1.5 text-[12px] font-medium px-2 py-0.5 rounded-full ${
                isDark ? "text-white/70 bg-white/5" : "text-black/70 bg-black/5"
              }`}
            >
              {airline.brandColor && (
                <span className="w-1.5 h-1.5 rounded-full" style={{ backgroundColor: airline.brandColor }} />
              )}
              {compactNav ? airline.shortName || airline.name : airline.name}
            </div>
          )}

          {/* Chapter links — hidden on small viewports, replaced by Contents */}
          <nav aria-label="Chapters" className={`${compactNav ? "hidden xl:flex gap-0.5" : "hidden md:flex gap-1"} items-center flex-1 min-w-0 overflow-hidden`}>
            {visibleChapters.map((c) => {
              const isOn = c.id === activeChapter;
              const isDemos = c.id === "demos";
              if (isDemos) {
                return (
                  <button
                    key={c.id}
                    onClick={() => jumpChapter(c.id)}
                    data-testid={`nav-chapter-${c.id}`}
                    className={`whitespace-nowrap ${chapterPad} h-8 rounded-full font-semibold transition-colors inline-flex items-center gap-1 ${
                      isOn
                        ? "bg-[#F2A900] text-[#1d1d1f]"
                        : "text-[#F2A900] hover:bg-[#F2A900]/10"
                    }`}
                  >
                    {c.label} <ArrowUpRight className="w-3 h-3" />
                  </button>
                );
              }
              return (
                <button
                  key={c.id}
                  onClick={() => jumpChapter(c.id)}
                  data-testid={`nav-chapter-${c.id}`}
                  className={`whitespace-nowrap ${chapterPad} h-8 rounded-full font-medium transition-colors ${
                    isOn
                      ? isDark
                        ? "bg-white text-[#0b0b0d]"
                        : "bg-[#1d1d1f] text-white"
                      : isDark
                      ? "text-white/60 hover:text-white hover:bg-white/[0.08]"
                      : "text-black/55 hover:text-[#1d1d1f] hover:bg-black/[0.05]"
                  }`}
                >
                  {c.label}
                </button>
              );
            })}
          </nav>

          {/* Right actions */}
          <div className="flex items-center gap-1 flex-shrink-0 ml-auto">
            <button
              onClick={() => setContentsOpen(true)}
              data-testid="nav-contents"
              aria-label="Contents"
              className={`flex items-center gap-1.5 px-3 h-8 rounded-full text-[13px] font-medium ${
                isDark ? "text-white/70 hover:text-white hover:bg-white/[0.08]" : "text-black/60 hover:text-[#1d1d1f] hover:bg-black/[0.05]"
              }`}
            >
              <Menu className="w-3.5 h-3.5" /> <span className={compactNav ? "hidden 2xl:inline" : ""}>Contents</span>
            </button>

            <div className={`hidden lg:block w-px h-5 mx-1 ${isDark ? "bg-white/10" : "bg-black/10"}`} />

            <IconButton
              testid="nav-share"
              title="Share"
              isDark={isDark}
              onClick={handleShare}
              toast={shareToast}
            >
              <Share2 className="w-4 h-4" />
            </IconButton>
            <IconButton
              testid="nav-present"
              title={isFullscreen ? "Exit Present" : "Present"}
              isDark={isDark}
              onClick={togglePresent}
              active={isFullscreen}
            >
              {isFullscreen ? <Minimize2 className="w-4 h-4" /> : <Maximize2 className="w-4 h-4" />}
            </IconButton>
            <IconButton
              testid="nav-theme"
              title={isDark ? "Light mode" : "Dark mode"}
              isDark={isDark}
              onClick={toggleTheme}
            >
              {isDark ? <Sun className="w-4 h-4" /> : <Moon className="w-4 h-4" />}
            </IconButton>
            {isPrivate && (
              <IconButton
                testid="nav-logout"
                title="Sign out"
                isDark={isDark}
                onClick={handleLogout}
              >
                <LogOut className="w-4 h-4" />
              </IconButton>
            )}
          </div>
        </div>
      </header>

      {contentsOpen && (
        <ContentsDrawer
          isDark={isDark}
          onClose={() => setContentsOpen(false)}
          slideList={slideList}
          currentSlide={currentSlide}
          setCurrentSlide={(i) => {
            setCurrentSlide(i);
            setContentsOpen(false);
          }}
          airline={airline}
        />
      )}

      {demosOpen && (
        <DemosDrawer isDark={isDark} airline={airline} onClose={() => setDemosOpen(false)} />
      )}
    </>
  );
};

const IconButton = ({ isDark, onClick, title, children, testid, toast, active }) => (
  <button
    onClick={onClick}
    title={title}
    data-testid={testid}
    className={`relative w-8 h-8 rounded-full flex items-center justify-center transition-colors ${
      active
        ? isDark ? "bg-white text-[#0b0b0d]" : "bg-[#1d1d1f] text-white"
        : isDark
          ? "text-white/70 hover:text-white hover:bg-white/[0.08]"
          : "text-black/60 hover:text-[#1d1d1f] hover:bg-black/[0.05]"
    }`}
  >
    {children}
    {toast && (
      <span className={`absolute top-9 right-0 whitespace-nowrap text-[11px] font-medium px-2 py-1 rounded-md ${
        isDark ? "bg-white text-[#0b0b0d]" : "bg-[#1d1d1f] text-white"
      }`}>
        {toast}
      </span>
    )}
  </button>
);

const ContentsDrawer = ({ isDark, onClose, slideList, currentSlide, setCurrentSlide, airline }) => {
  return createPortal(
    <div className="fixed inset-0 z-50 flex items-start justify-center" onClick={onClose}>
      <div className={`absolute inset-0 ${isDark ? "bg-black/70" : "bg-black/40"}`} style={{ backdropFilter: "blur(8px)" }} />
      <div
        onClick={(e) => e.stopPropagation()}
        className={`relative mt-16 w-full max-w-2xl mx-6 rounded-3xl overflow-hidden ${
          isDark ? "bg-[#0b0b0d] border border-white/10" : "bg-[#fbfbf9] border border-black/10"
        }`}
        style={{ animation: "fadeIn 0.25s ease-out" }}
      >
        <div className={`px-8 pt-7 pb-4 flex items-center justify-between border-b ${isDark ? "border-white/10" : "border-black/10"}`}>
          <div>
            <div className={`k-eyebrow ${isDark ? "text-white/60" : "text-black/50"}`}>Contents</div>
            <div className={`text-[22px] font-semibold tracking-tight mt-1 ${isDark ? "text-white" : "text-[#1d1d1f]"}`}>
              {airline?.name || "Aeroplay"}
            </div>
          </div>
          <button onClick={onClose} className={`w-9 h-9 rounded-full flex items-center justify-center ${
            isDark ? "text-white/70 hover:bg-white/10" : "text-black/60 hover:bg-black/5"
          }`} data-testid="contents-close">
            <X className="w-5 h-5" />
          </button>
        </div>
        <div className="max-h-[70vh] overflow-y-auto py-3">
          {slideList.map((s, i) => {
            const isOn = i === currentSlide;
            const idxLabel = i === 0 ? "" : String(i).padStart(2, "0");
            return (
              <button
                key={s.id}
                onClick={() => setCurrentSlide(i)}
                data-testid={`contents-item-${s.id}`}
                className={`w-full flex items-baseline gap-6 px-8 py-3 text-left transition-colors ${
                  isDark
                    ? isOn ? "bg-white/[0.06]" : "hover:bg-white/[0.04]"
                    : isOn ? "bg-black/[0.04]" : "hover:bg-black/[0.03]"
                }`}
              >
                <span className={`k-eyebrow ${isDark ? "text-white/35" : "text-black/35"}`}>
                  {idxLabel || "—"}
                </span>
                <span className={`text-[18px] font-medium tracking-tight ${
                  isOn
                    ? (isDark ? "text-white" : "text-[#1d1d1f]")
                    : (isDark ? "text-white/70" : "text-black/70")
                }`}>
                  {s.title}
                </span>
              </button>
            );
          })}
        </div>
      </div>
    </div>,
    document.body
  );
};

const DemosDrawer = ({ isDark, airline, onClose }) => {
  const isPrivate = airline && airline.id !== "default";
  const items = [
    {
      id: "bridge",
      label: "NucleusOS Bridge",
      caption: "AI-assisted content operations",
      href: "https://bridge.nucleus-alpha.com/",
      icon: <img src={NUCLEUS_BRIDGE} alt="NucleusOS Bridge" className="h-6" />,
    },
    ...(!airline.hideLetsPlay
      ? [
          {
            id: "letsplay",
            label: "Let's Play",
            caption: "The passenger experience — full IFE preview",
            href: "https://lounge.charlie.stg.c.airbus.dev/",
            icon: <img src={AEROPLAY_ICON} alt="Aeroplay" className="h-6 w-6 rounded-md" />,
          },
        ]
      : []),
    ...(airline.extraCTAs || []).map((cta) => ({
      id: cta.id,
      label: cta.label,
      caption:
        cta.useNucleusLogo
          ? "Live analytics & content telemetry"
          : cta.branded
          ? `Branded ${airline.name} IFE experience`
          : "Live demo",
      href: cta.href,
      icon: cta.useNucleusLogo
        ? <img src={NUCLEUS_LENS} alt="NucleusOS Lens" className="h-6" />
        : cta.useAirlineIcon && airline.icon
        ? <img src={airline.icon} alt={airline.name} className="h-6 w-6 object-contain" />
        : <img src={AEROPLAY_ICON} alt="Aeroplay" className="h-6 w-6 rounded-md" />,
    })),
  ];

  return createPortal(
    <div className="fixed inset-0 z-50 flex items-start justify-center" onClick={onClose}>
      <div className={`absolute inset-0 ${isDark ? "bg-black/70" : "bg-black/40"}`} style={{ backdropFilter: "blur(8px)" }} />
      <div
        onClick={(e) => e.stopPropagation()}
        className={`relative mt-16 w-full max-w-2xl mx-6 rounded-3xl overflow-hidden ${
          isDark ? "bg-[#0b0b0d] border border-white/10" : "bg-[#fbfbf9] border border-black/10"
        }`}
        style={{ animation: "fadeIn 0.25s ease-out" }}
      >
        <div className={`px-8 pt-7 pb-5 border-b ${isDark ? "border-white/10" : "border-black/10"}`}>
          <div className="flex items-start justify-between gap-4">
            <div>
              <div className={`k-eyebrow ${isDark ? "text-white/60" : "text-black/50"}`}>Live demos</div>
              <div className={`text-[22px] font-semibold tracking-tight mt-1 ${isDark ? "text-white" : "text-[#1d1d1f]"}`}>
                See it in action
              </div>
              <p className={`text-[15px] mt-1 ${isDark ? "text-white/60" : "text-black/55"}`}>
                {isPrivate
                  ? `Products tuned for ${airline.name}.`
                  : "Pick a product to explore live."}
              </p>
            </div>
            <button onClick={onClose} className={`w-9 h-9 rounded-full flex items-center justify-center flex-shrink-0 ${
              isDark ? "text-white/70 hover:bg-white/10" : "text-black/60 hover:bg-black/5"
            }`} data-testid="demos-close">
              <X className="w-5 h-5" />
            </button>
          </div>
        </div>

        <div className="py-2">
          {items.map((it) => (
            <a
              key={it.id}
              href={it.href}
              target="_blank"
              rel="noopener noreferrer"
              data-testid={`demo-${it.id}`}
              className={`group flex items-center gap-4 px-8 py-4 transition-colors ${
                isDark ? "hover:bg-white/[0.04]" : "hover:bg-black/[0.03]"
              }`}
            >
              <div className={`w-10 h-10 rounded-xl flex items-center justify-center flex-shrink-0 ${
                isDark ? "bg-white/5" : "bg-black/5"
              }`}>
                {it.icon}
              </div>
              <div className="flex-1 min-w-0">
                <div className={`text-[16px] font-semibold ${isDark ? "text-white" : "text-[#1d1d1f]"}`}>{it.label}</div>
                <div className={`text-[13px] ${isDark ? "text-white/50" : "text-black/50"}`}>{it.caption}</div>
              </div>
              <ArrowUpRight className={`w-5 h-5 flex-shrink-0 transition-transform group-hover:translate-x-0.5 group-hover:-translate-y-0.5 ${
                isDark ? "text-white/40" : "text-black/40"
              }`} />
            </a>
          ))}
        </div>
      </div>
    </div>,
    document.body
  );
};

export default PitchNav;
